import { Dispatch, useCallback, useEffect, useRef } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { FormSpy } from 'react-final-form';
import { ARRAY_ERROR, FieldState } from 'final-form';

import { catalogGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { CatalogStatusesEnum } from 'CorePackage/CatalogsModule/enums/CatalogStatusesEnum';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IErknmKindResultDecisionSelectOption } from 'CorePackage/CatalogsModule/interfaces/IErknmKindResultDecisionSelectOption';
import {
  getCatalogsErknmKindResultDecisionSelectOptions,
  getCatalogsErknmKindResultDecisionStatus,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmExternalParams,
  getKnmFormRef,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsNeedActSaveOrSign,
  getKnmPmIsUnableToRefuseFormValue,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import {
  PM_RESULT_DECISIONS_BLOCK_ID,
  getPmResultDecisionsBlockId,
} from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import {
  IKnmPmFormResultDecision,
  IKnmPmSubjectTypeSelectOption,
} from 'KnmPmPackage/MainModule/interfaces/IKnmPmFormResultDecision';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { getDirtyActGuid } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { KnmResultDecisionSubjectTypesEnum } from 'KnmPackage/KnmSubjectModule/enums/KnmResultDecisionSubjectTypesEnum';

import { PmResultOfInspectionPmResultDecisionsCollapse } from './PmResultOfInspectionPmResultDecisionsCollapse';

interface IPmResultOfInspectionPmResultDecisionsStoreProps {
  formRef: IKnmFormRef | undefined;
  kindDecisionSelectOptions?: IErknmKindResultDecisionSelectOption[];
  kindDecisionStatus: CatalogStatusesEnum;
  isKnmStatusTypeCompleted: boolean;
  dirtyActGuid: string | undefined;
  isNeedActSaveOrSign?: boolean;
  knmExternalParams?: Record<string, string>;
  isUnableToRefuse?: boolean;
}

interface IPmResultOfInspectionPmResultDecisionsProps extends IPmResultOfInspectionPmResultDecisionsStoreProps {
  watchOnly: boolean;
  withoutIssue?: boolean;
  isPaperless?: boolean;
  pmSubject?: IKnmErknmFormSubject;
  values?: IKnmPmForm;
  clearWatchOnly: boolean;
  resigningRequired: boolean;
}

export const resultDecisionSubjectTypeOptions: IKnmPmSubjectTypeSelectOption[] = [
  {
    id: KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT,
    value: KnmResultDecisionSubjectTypesEnum.CONTROLLING_SUBJECT,
    label: 'Контролируемое лицо',
  },
  {
    id: KnmResultDecisionSubjectTypesEnum.FOUNDER,
    value: KnmResultDecisionSubjectTypesEnum.FOUNDER,
    label: 'Учредитель',
  },
];

const PmResultOfInspectionPmResultDecisionsWithoutStore = ({
  formRef,
  watchOnly,
  withoutIssue,
  kindDecisionSelectOptions,
  kindDecisionStatus,
  pmSubject,
  isPaperless,
  values,
  clearWatchOnly,
  isKnmStatusTypeCompleted,
  resigningRequired,
  dirtyActGuid,
  isNeedActSaveOrSign = false,
  knmExternalParams,
  isUnableToRefuse,
}: IPmResultOfInspectionPmResultDecisionsProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const dirtyActGuidRef = useRef(dirtyActGuid);

  const lateFieldName = '/pmResultDecisions';

  const isExpandAllInPrescription = knmExternalParams?.expandAllInPrescription === 'true';

  /**
   * resigningRequired не закрывает редактирование уже внесенных решений, а только
   * запрещает добавлять и удалять их до переподписания паспорта. Так же сделано
   * в КНМ (ErknmActs) и в паспорте решения (DecisionAct).
   */
  const makeRemoveKnmObjectHandler = (index: number) => () =>
    !watchOnly && !resigningRequired && remove && remove('pmResultDecisions', index);

  const addPmResultDecision = useCallback(() => {
    if (!watchOnly && push) {
      const kindDecision =
        kindDecisionSelectOptions?.find(item => item.code === 'D_7') ||
        kindDecisionSelectOptions?.find(item => item.code === 'D_1');

      const addingObject: IKnmPmFormResultDecision = {
        listId: uuidv4(),
        isNew: true,
        kindDecision:
          values?.act?.paperless &&
          (!values?.act.familiarizationNotifications || values?.act.familiarizationNotifications?.length === 0)
            ? undefined
            : kindDecision,
        subjectType: resultDecisionSubjectTypeOptions[0],
        paperless: values?.act?.paperless,
        organization: {
          ...pmSubject,
          subjectTypePm: pmSubject?.subjectType,
          locationRegionCodeFromFns: pmSubject?.locationRegionCodeFromFns,
        },
        number:
          kindDecision && values?.act?.numberAct
            ? `${values?.act?.numberAct}-${values?.pmResultDecisions?.length || '1'}`
            : undefined,
        firstExtractDateTime: null,
        systemSignatureUsed: false,
        systemSignatureMnemonic: null,
        systemSignatureDateTime: null,
        systemSignatureTitle: null,
        violatedMandatoryRequirements: [],
      };

      push('pmResultDecisions', addingObject);
    }
  }, [watchOnly, push, kindDecisionSelectOptions, pmSubject, values]);

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.ERKNM_KIND_RESULT_DECISION));
  }, [dispatch]);

  useEffect(() => {
    dirtyActGuidRef.current = dirtyActGuid;
  }, [dirtyActGuid]);

  const validateArray = useCallback(
    (
      rds: IKnmPmFormResultDecision[] | undefined,
      allValues: IKnmPmForm,
      meta: FieldState<IKnmPmFormResultDecision[]> | undefined,
    ) => {
      if (
        isKnmStatusTypeCompleted &&
        !withoutIssue &&
        !rds?.find(result => result.kindDecision?.code === KindResultDecisionCodesEnum.D_7) &&
        !resigningRequired &&
        meta?.touched &&
        (values?.act?.paperless ? !dirtyActGuidRef.current : true)
      ) {
        return 'Необходимо заполнить предписание';
      }
    },
    [isKnmStatusTypeCompleted, resigningRequired, values, dirtyActGuidRef.current],
  );

  return (
    <>
      {isUnableToRefuse && (
        <FieldBlock
          id={PM_RESULT_DECISIONS_BLOCK_ID}
          label="Сведения о решениях, принятых по результатам ПМ"
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              onClick={addPmResultDecision}
              disabled={
                watchOnly ||
                withoutIssue ||
                kindDecisionStatus !== CatalogStatusesEnum.SUCCEEDED ||
                isNeedActSaveOrSign ||
                resigningRequired
              }
            />
          }
        />
      )}
      <FormSpy subscription={{ errors: true, touched: true }}>
        {props => {
          const fieldError = props.errors?.pmResultDecisions?.[ARRAY_ERROR];
          const isFieldTouched = props.touched?.pmResultDecisions;

          if (isFieldTouched && !Array.isArray(fieldError) && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
          }
          if (isFieldTouched && Array.isArray(fieldError) && fieldError) {
            return <p className="shared-input-error shared-row__small-margin">{fieldError[0]}</p>;
          }

          return null;
        }}
      </FormSpy>
      <FieldArray name="pmResultDecisions" validate={validateArray}>
        {({ fields }: FieldArrayRenderProps<IKnmPmFormResultDecision, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          return (
            <Row>
              {fields.map((name, index) => {
                const fieldValue = fields.value?.[index];
                const id = getPmResultDecisionsBlockId(fieldValue.listId);

                return (
                  <PmResultOfInspectionPmResultDecisionsCollapse
                    key={id}
                    id={id}
                    name={name}
                    value={fieldValue}
                    watchOnly={watchOnly}
                    onRemove={makeRemoveKnmObjectHandler(index)}
                    pmSubject={pmSubject}
                    resultIndex={index}
                    lateFieldName={`${lateFieldName}/${fieldValue?.guid}`}
                    isPaperless={!!isPaperless}
                    values={values}
                    clearWatchOnly={clearWatchOnly}
                    isExpandAllInPrescription={isExpandAllInPrescription}
                  />
                );
              })}
            </Row>
          );
        }}
      </FieldArray>
    </>
  );
};

const mapStateToProps = (state: IRootState): IPmResultOfInspectionPmResultDecisionsStoreProps => ({
  formRef: getKnmFormRef(state),
  kindDecisionSelectOptions: getCatalogsErknmKindResultDecisionSelectOptions(state),
  kindDecisionStatus: getCatalogsErknmKindResultDecisionStatus(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  dirtyActGuid: getDirtyActGuid(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  knmExternalParams: getKnmExternalParams(state),
  isUnableToRefuse: getKnmPmIsUnableToRefuseFormValue(state),
});

export const PmResultOfInspectionPmResultDecisions = connect(mapStateToProps)(
  PmResultOfInspectionPmResultDecisionsWithoutStore,
);
